import React from 'react';
import { Link } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import Badge from '../components/Badge';
import { IconArrowRight } from '../components/Icons';

const RING_RADIUS = 26;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

const AcademyHome = () => {
  const { topics, getCourseProgress, getCourseSteps, getAllBadges, getTopicLessonCount } = useAcademy();

  const badges = getAllBadges();
  const earnedBadgeCount = badges.filter((b) => b.earned).length;

  // Overall score across every course, based on the number of individual
  // tasks/steps (videos + exercises) the user has completed.
  let totalSteps = 0;
  let doneSteps = 0;
  topics.forEach((topic) => {
    topic.courses.forEach((course) => {
      const steps = getCourseSteps(topic.id, course);
      totalSteps += steps.length;
      doneSteps += steps.filter((s) => s.done).length;
    });
  });
  const overallPct = totalSteps ? Math.round((doneSteps / totalSteps) * 100) : 0;
  const ringOffset = RING_CIRC * (1 - overallPct / 100);

  return (
    <main className="ac-page">
      
      <div className="ac-section-head">
        <h2 className="ac-section-title">All Courses</h2>
        <div className="ac-overall">
          <div className="ac-overall-text">
            <span className="ac-overall-title">Overall progress</span>
            <span className="ac-overall-sub">
              {doneSteps} of {totalSteps} tasks done · {topics.length} tracks
            </span>
          </div>
          <div className="ac-ring-wrap" aria-label={`${overallPct}% complete`}>
            <svg className="ac-ring" width="64" height="64" viewBox="0 0 64 64">
              <circle className="ac-ring-track" cx="32" cy="32" r={RING_RADIUS} />
              <circle
                className="ac-ring-fill"
                cx="32"
                cy="32"
                r={RING_RADIUS}
                style={{ strokeDasharray: RING_CIRC, strokeDashoffset: ringOffset }}
              />
            </svg>
            <span className="ac-ring-pct">{overallPct}%</span>
          </div>
        </div>
      </div>

      {topics.length === 0 && (
        <p className="ac-boot" style={{ minHeight: '30vh' }}>
          Noch keine Inhalte verfügbar.
        </p>
      )}

      <div className="ac-track-grid">
        {topics.map((topic, i) => {
          const courseCount = topic.courses.length;
          const lessonCount = getTopicLessonCount(topic);
          const completedCourses = topic.courses.filter(
            (c) => getCourseProgress(topic.id, c).isComplete
          ).length;
          return (
            <Link
              key={topic.id}
              to={`/academy/${topic.id}`}
              className="ac-track-card"
              style={{ '--ac-card-accent': topic.accent }}
            >
              <div className="ac-track-media">
                <img src={topic.image} alt={topic.title} className="ac-track-img" />
                <span className="ac-track-overlay" />
                <span className="ac-track-no">{String(i + 1).padStart(2, '0')}</span>
              </div>

              <div className="ac-track-body">
                <span className="ac-track-tag">{topic.tagline}</span>
                <h3 className="ac-track-title">{topic.title}</h3>
                <p className="ac-track-desc">{topic.description}</p>

                <div className="ac-track-foot">
                  <div className="ac-track-stats">
                    <span className="ac-track-stat">
                      <strong>{courseCount}</strong> {courseCount === 1 ? 'Course' : 'Courses'}
                    </span>
                    <span className="ac-track-stat-sep" />
                    <span className="ac-track-stat">
                      <strong>{lessonCount}</strong> {lessonCount === 1 ? 'Lesson' : 'Lessons'}
                    </span>
                    {completedCourses > 0 && (
                      <>
                        <span className="ac-track-stat-sep" />
                        <span className="ac-track-stat ac-track-stat-done">
                          <strong>{completedCourses}</strong> done
                        </span>
                      </>
                    )}
                  </div>
                  <span className="ac-track-arrow">
                    Enter track <IconArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {badges.length > 0 && (
        <>
          <div className="ac-section-head ac-section-head-badges">
            <h2 className="ac-section-title">Your Badges</h2>
            <span className="ac-section-meta">
              {earnedBadgeCount} of {badges.length} earned
            </span>
          </div>
          <div className="ac-bdg-grid">
            {badges.map((badge) => (
              <Badge key={badge.id} badge={badge} earned={badge.earned} />
            ))}
          </div>
        </>
      )}
    </main>
  );
};

export default AcademyHome;
